import * as React from "react"
import { createPortal } from "react-dom"
import { cn } from "../../lib/utils"

interface TooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  delayMs?: number
  className?: string
}

const Tooltip = ({ content, children, delayMs = 400, className }: TooltipProps) => {
  const [isVisible, setIsVisible] = React.useState(false)
  const [coords, setCoords] = React.useState({ top: 0, left: 0 })
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const tooltipRef = React.useRef<HTMLDivElement>(null)

  const showTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        const tooltipHeight = 220 // Approximate height of tooltip
        const tooltipWidth = 300

        // Check space below vs above
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top

        // Prefer bottom, use top only if more space above AND not enough below
        const useBottom = spaceBelow >= tooltipHeight || spaceBelow >= spaceAbove

        // Calculate position
        let top: number
        if (useBottom) {
          top = rect.bottom + 8
        } else {
          top = rect.top - tooltipHeight - 8
        }

        // Center horizontally on trigger, but keep within viewport
        let left = rect.left + rect.width / 2 - tooltipWidth / 2
        left = Math.max(10, Math.min(left, window.innerWidth - tooltipWidth - 10))

        setCoords({ top, left })
        setIsVisible(true)
      }
    }, delayMs)
  }

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsVisible(false)
  }

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const tooltipContent = isVisible ? (
    <div
      ref={tooltipRef}
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        zIndex: 9999,
      }}
      className={cn(
        "w-[300px] px-3 py-2.5 text-sm",
        "bg-popover text-popover-foreground border border-border rounded-lg shadow-xl",
        "animate-in fade-in-0 zoom-in-95 duration-200",
        className
      )}
    >
      {content}
    </div>
  ) : null

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-flex"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {children}
      </div>
      {typeof document !== 'undefined' && createPortal(tooltipContent, document.body)}
    </>
  )
}

export { Tooltip }
